import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";

// Cosmetic sparkline — pseudo-random shape seeded by the accent color.
function trendFor(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return Array.from({ length: 14 }).map((_, i) => ({ i, v: 40 + ((h + i * 13) % 40) + (i % 3) * 6 }));
}

function Spark({ color = "#0047FF" }) {
  const data = trendFor(color);
  return (
    <div className="h-10 -mx-2 -mb-1">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id={`sg-${color}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#sg-${color})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function KpiCard({ label, value, delta, direction = "up", accent = "#0047FF", note, index = 0, testid }) {
  const TrendIcon = direction === "up" ? TrendingUp : TrendingDown;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className="glass rounded-2xl p-5 relative overflow-hidden group hover:-translate-y-0.5 transition-transform"
      data-testid={testid}
    >
      <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full opacity-30 blur-2xl" style={{ background: accent }} />
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <div className="h-6 w-6 rounded-md grid place-items-center" style={{ background: `${accent}18`, color: accent }}>
          <Activity className="h-3.5 w-3.5" strokeWidth={2} />
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="metric text-4xl">{value}</div>
        {delta && (
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-medium ${direction === "up" ? "text-emerald-600" : "text-rose-600"}`}
          >
            <TrendIcon className="h-3 w-3" /> {delta}
          </span>
        )}
      </div>
      {note && <div className="text-xs text-muted-foreground mt-1">{note}</div>}
      <Spark color={accent} />
    </motion.div>
  );
}

export default function KpiGrid({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kpi-grid">
      {items.map((k, i) => (
        <KpiCard key={k.label} index={i} testid={`kpi-${k.label.toLowerCase().replace(/\s/g,'-')}`} {...k} />
      ))}
    </div>
  );
}
